import { supabase } from '@/lib/supabase';

export interface HistoricoCategoria {
  funcionario_id: string;
  categoria: string;
  data_inicio: string;
}

/**
 * Determina a categoria de um funcionário numa determinada data.
 * Consulta o histórico de categorias e retorna a categoria vigente na data.
 * Se não houver histórico ou a data for anterior ao primeiro registo,
 * retorna a categoria atual do funcionário.
 */
export async function getCategoriaNaData(
  funcionarioId: string,
  dataRef: string
): Promise<string | null> {
  // Buscar histórico de categorias para este funcionário
  const { data } = await supabase
    .from('historico_categorias')
    .select('categoria, data_inicio')
    .eq('funcionario_id', funcionarioId)
    .lte('data_inicio', dataRef)
    .order('data_inicio', { ascending: false })
    .limit(1);

  if (data && data.length > 0) {
    return data[0].categoria;
  }

  // Se não há histórico para esta data, buscar categoria atual do funcionário
  const { data: funcData } = await supabase
    .from('funcionarios')
    .select('categoria')
    .eq('id', funcionarioId)
    .maybeSingle();

  return (funcData?.categoria as string) || null;
}

/**
 * Carrega todo o histórico de categorias para um conjunto de funcionários.
 * Retorna um mapa: funcionario_id -> array de {categoria, data_inicio}
 */
export async function carregarHistoricoCategorias(
  funcionarioIds: string[]
): Promise<Record<string, HistoricoCategoria[]>> {
  if (funcionarioIds.length === 0) return {};

  const { data } = await supabase
    .from('historico_categorias')
    .select('funcionario_id, categoria, data_inicio')
    .in('funcionario_id', funcionarioIds)
    .order('data_inicio', { ascending: false });

  const map: Record<string, HistoricoCategoria[]> = {};
  (data ?? []).forEach((row) => {
    const id = row.funcionario_id as string;
    if (!map[id]) map[id] = [];
    map[id].push({
      funcionario_id: id,
      categoria: row.categoria as string,
      data_inicio: row.data_inicio as string,
    });
  });

  return map;
}

/**
 * Determina a categoria de um funcionário numa data, usando o mapa de histórico.
 * Mais eficiente que getCategoriaNaData quando já se tem o mapa carregado.
 */
export function getCategoriaDoHistorico(
  funcionarioId: string,
  dataRef: string,
  historicoMap: Record<string, HistoricoCategoria[]>,
  categoriaAtual?: string | null
): string {
  const historico = historicoMap[funcionarioId] || [];

  // Encontrar a categoria mais recente com data_inicio <= dataRef
  for (const entry of historico) {
    if (entry.data_inicio <= dataRef) {
      return entry.categoria;
    }
  }

  // Se não há histórico para esta data, usar categoria atual
  return categoriaAtual || 'Sem Categoria';
}